<template>
  <div
    data-testid="public-template-3"
    class="bg-t3-bg-dark text-slate-100 antialiased font-t3-display rms-template-3-root min-h-screen"
    :style="wrapperStyle"
  >
    <Template3Header
      :name="restaurant.name"
      :viewer="restaurant.viewer"
      :owner-view-mode="ownerViewMode"
      :languages="languages"
      :current-locale="currentLocale"
      :reservation-available="restaurant.reservation_available === true"
      :slug="restaurant.slug || ''"
      :primary-color="restaurant.primary_color || null"
      :view-list-label="viewListLabel"
      :nav-label="navLabel"
      :view-list-aria-label="viewListAriaLabel"
      :reserve-button-label="reserveButtonLabel"
      :reserve-modal-heading="reserveModalHeading"
      :reserve-pax-label="reservePaxLabel"
      :feedback-available="restaurant.feedback_available === true"
      @select-locale="$emit('select-locale', $event)"
    />
    <main class="space-y-0">
      <Template3Hero
        :name="restaurant.name"
        :tagline="restaurant.tagline"
        :description="(restaurant.description || '').trim()"
        :banner-url="restaurant.banner_url"
        :logo-url="restaurant.logo_url || ''"
        :operating-hours="effectiveOperatingHours"
        :view-list-label="viewListLabel"
        :has-about="hasAbout"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
      />
      <div v-motion :initial="sectionMotion.initial" :enter="sectionMotion.enter">
        <Template3Menu
          :menu-groups="restaurant.menu_groups || []"
          :menu-items="restaurant.menu_items || []"
          :currency="restaurant.currency || 'USD'"
          :show-currency="restaurant.show_currency !== false"
          :section-title="menuSectionTitle"
          :empty-message="menuEmptyMessage"
          :show-owner-edit-fab="ownerViewMode"
          @select-item="onSelectMenuItem"
        />
      </div>
      <PublicMenuItemDetailModal
        v-model="detailOpen"
        v-model:selected-modifier-uuids="detailModifierUuids"
        :item="detailItem"
        :selected-sku="detailSelectedSku"
        :category-name="detailCategoryName"
        :primary-color="restaurant.primary_color"
        :currency="restaurant.currency || 'USD'"
        :show-currency="restaurant.show_currency !== false"
        :quick-order-available="!!injectedQuickOrderAvailable"
        :cart-item-quantity="detailCartQty"
        :business-type="restaurant.business_type || 'restaurants'"
        :require-variant-selection="detailRequiresVariantSelection"
        @update:model-value="onDetailClose"
        @update:selected-sku="onDetailSelectedSku"
        @add-to-cart="onAddToCart"
      />
      <template v-if="restaurant.feedback_available">
        <section
          id="reviews"
          v-motion
          class="border-y border-white/10"
          aria-label="Reviews"
          :initial="sectionMotion.initial"
          :enter="sectionMotion.enter"
        >
          <Template3Reviews
            :feedbacks="restaurant.feedbacks || []"
            :empty-message="$t('public.noReviewsYet')"
          />
        </section>
        <div v-motion :initial="sectionMotion.initial" :enter="sectionMotion.enter">
          <slot name="feedback-form" />
        </div>
      </template>
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        class="border-y border-white/10"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
            <p class="text-sm font-mono text-slate-300">{{ $t('public.noFeedbackAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <div v-motion :initial="sectionMotion.initial" :enter="sectionMotion.enterAbout">
        <Template3About
        :text="(restaurant.description || '').trim()"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
      />
      </div>
      <div
        v-if="hasOperatingHours"
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enterContact"
      >
        <Template3Contact :operating-hours="effectiveOperatingHours" />
      </div>
      <PublicMailingListSection
        v-if="restaurant.mailing_list_available"
        :restaurant="restaurant"
        template="template-3"
      />
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
            <p class="text-sm font-mono text-slate-300">{{ $t('public.noMailingListAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <Template3Map
        :restaurant="restaurant"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
      />
    </main>
    <div v-motion :initial="footerMotion.initial" :enter="footerMotion.enter">
      <Template3Footer
        :restaurant-name="restaurant.name"
        :logo-url="restaurant.logo_url || ''"
        :show-credits="!restaurant.hide_footer_credits"
        :modal-locale="restaurant.locale || restaurant.default_locale || 'en'"
        :view-list-label="viewListLabel"
        :footer-tagline="footerTagline"
        :feedback-available="restaurant.feedback_available === true"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, inject } from 'vue'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { DAY_KEYS, hasOperatingHoursSchedule } from '@/utils/availability'
import { restaurantHasMapOrLink } from '@/utils/googleMapsUrl'
import Template3Header from '@/components/public/templates/template-3/Template3Header.vue'
import Template3Hero from '@/components/public/templates/template-3/Template3Hero.vue'
import Template3Menu from '@/components/public/templates/template-3/Template3Menu.vue'
import Template3Reviews from '@/components/public/templates/template-3/Template3Reviews.vue'
import Template3About from '@/components/public/templates/template-3/Template3About.vue'
import Template3Contact from '@/components/public/templates/template-3/Template3Contact.vue'
import Template3Map from '@/components/public/templates/template-3/Template3Map.vue'
import Template3Footer from '@/components/public/templates/template-3/Template3Footer.vue'
import PublicMailingListSection from '@/components/public/PublicMailingListSection.vue'
import PublicMenuItemDetailModal from '@/components/public/PublicMenuItemDetailModal.vue'
import { buildQuickOrderCartLinePayload } from '@/utils/publicMenuCart.js'
import { useToastStore } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { useBusinessTypeLabels } from '@/composables/useBusinessTypeLabels'

const { t } = useI18n()
const toastStore = useToastStore()
const { sectionTitle: menuSectionTitle, emptyMessage: menuEmptyMessage, viewListLabel, navLabel, viewListAriaLabel, reserveButtonLabel, reserveModalHeading, reservePaxLabel, footerTagline } = useBusinessTypeLabels(() => props.restaurant, t)
const injectedCart = inject('quickOrderCart', ref(null))
const injectedQuickOrderAvailable = inject('quickOrderAvailable', ref(false))

const props = defineProps({
  restaurant: { type: Object, required: true },
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
})

defineEmits(['select-locale'])

/** Default business logo on public sites when the restaurant has no logo set. */
const DEFAULT_HOURS = Object.freeze(
  Object.fromEntries(DAY_KEYS.map(day => [day, { open: true, slots: [{ from: '09:00', to: '21:00' }] }]))
)

const effectiveOperatingHours = computed(() => {
  const hours = props.restaurant.operating_hours
  if (hours == null) return null
  return typeof hours === 'object' && Object.keys(hours).length > 0 ? hours : DEFAULT_HOURS
})

const detailItem = ref(null)
const detailSelectedSku = ref(null)
const detailCategoryName = ref('Menu')
const detailOpen = ref(false)
const detailModifierUuids = ref([])
const detailRequiresVariantSelection = computed(
  () => detailItem.value?.type === 'with_variants' && detailSelectedSku.value == null,
)
let detailTriggerEl = null

function onSelectMenuItem(payload) {
  detailItem.value = payload?.item ?? null
  detailSelectedSku.value = payload?.sku ?? null
  detailCategoryName.value = payload?.categoryName ?? 'Menu'
  detailTriggerEl = payload?.trigger ?? null
  detailOpen.value = true
}

const detailCartQty = computed(() => {
  const c = injectedCart.value
  if (!c || !detailItem.value?.uuid) return 0
  return c.getItemQuantity(
    detailItem.value.uuid,
    detailSelectedSku.value?.uuid ?? null,
    detailModifierUuids.value,
  )
})

function onAddToCart({ item, quantity, selectedSku, modifier_uuids, replace }) {
  const c = injectedCart.value
  if (!c || !item) return
  const line = buildQuickOrderCartLinePayload({
    item,
    selectedSku,
    modifier_uuids: modifier_uuids ?? [],
  })
  const isUpdate = c.getItemQuantity(item.uuid, line.variant_uuid, line.modifier_uuids) > 0
  c.addItem({ ...line, quantity, replace: replace === true })
  toastStore.success(t(isUpdate ? 'public.quickOrder.itemUpdated' : 'public.quickOrder.itemAdded'))
}

function onDetailSelectedSku(sku) {
  detailSelectedSku.value = sku
}

function onDetailClose(open) {
  if (!open) {
    detailItem.value = null
    detailSelectedSku.value = null
    detailCategoryName.value = 'Menu'
    detailModifierUuids.value = []
    nextTick(() => {
      if (detailTriggerEl && typeof detailTriggerEl.focus === 'function') {
        detailTriggerEl.focus()
      }
      detailTriggerEl = null
    })
  }
}

const reducedMotion = useReducedMotion()

const sectionMotion = computed(() => {
  if (reducedMotion.value) {
    return {
      initial: { opacity: 1, y: 0 },
      enter: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterAbout: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterContact: { opacity: 1, y: 0, transition: { duration: 0 } },
    }
  }
  return {
    initial: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 80 } },
    enterAbout: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
    enterContact: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
  }
})

const footerMotion = computed(() => {
  if (reducedMotion.value) {
    return { initial: { opacity: 1 }, enter: { opacity: 1, transition: { duration: 0 } } }
  }
  return { initial: { opacity: 0 }, enter: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } } }
})

const hasMapOrLink = computed(() => restaurantHasMapOrLink(props.restaurant))

const hasOperatingHours = computed(() => hasOperatingHoursSchedule(effectiveOperatingHours.value))

const hasAbout = computed(() => ((props.restaurant?.description || '').trim().length > 0))

const t3PrimaryColor = computed(() => {
  const c = props.restaurant?.primary_color
  if (typeof c === 'string' && c.trim().length > 0) return c.trim()
  return '#f2b90d'
})

// String style so --t3-primary and --rms-public-primary are always in the DOM (primary color from restaurant)
const wrapperStyle = computed(() => {
  const primary = t3PrimaryColor.value
  return `--rms-public-primary: ${primary}; --t3-primary: ${primary}; --t3-bg-dark: #1a1c23; --t3-accent-dark: #2d3139`
})
</script>
